import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Bookmark, Check, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { getLesson, type LessonDetail } from "../api/courses";
import {
  completeLesson,
  lessonProgress,
  myModuleView,
  type LessonProgressOut,
  type ModuleLessonViewOut,
  type StudentModuleViewOut,
} from "../api/assignments";
import { ApiError } from "../api/client";
import { LessonMarkdown } from "../components/LessonMarkdown";
import { useCurrentUser } from "../hooks/useCurrentUser";

const LESSON_TYPE_LABEL: Record<string, string> = {
  text: "Теория",
  video: "Видео",
  file: "Материал",
  checklist: "Чек-лист",
  quiz: "Тест",
  mixed: "Урок",
};

export function StudentLessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const id = Number(lessonId);
  const [search] = useSearchParams();
  const moduleIdParam = search.get("module");
  const moduleId = moduleIdParam ? Number(moduleIdParam) : null;
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [progress, setProgress] = useState<LessonProgressOut | null>(null);
  const [moduleView, setModuleView] = useState<StudentModuleViewOut | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный урок");
      return;
    }
    setError(null);
    try {
      const [l, p, mv] = await Promise.all([
        getLesson(id),
        lessonProgress(id),
        moduleId && Number.isFinite(moduleId)
          ? myModuleView(moduleId).catch(() => null)
          : Promise.resolve(null),
      ]);
      setLesson(l);
      setProgress(p);
      setModuleView(mv);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404)
        setError("Урок не найден");
      else
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
    }
  }, [id, moduleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const siblings = useMemo(() => {
    if (!moduleView || !lesson) return { prev: null, next: null, index: -1 };
    const lessons = moduleView.lessons;
    const idx = lessons.findIndex((l) => l.lesson_id === lesson.id);
    if (idx < 0) return { prev: null, next: null, index: -1 };
    return {
      prev: idx > 0 ? lessons[idx - 1] ?? null : null,
      next: idx < lessons.length - 1 ? lessons[idx + 1] ?? null : null,
      index: idx,
    };
  }, [moduleView, lesson]);

  async function handleNext() {
    if (!lesson) return;
    setAdvancing(true);
    try {
      if (progress?.status !== "completed") {
        const p = await completeLesson(lesson.id);
        setProgress(p);
      }
      if (siblings.next && moduleView) {
        navigate(
          `/learn/lessons/${siblings.next.lesson_id}?module=${moduleView.module_id}`,
        );
      } else if (moduleView) {
        // Last lesson of module — back to module page
        navigate(`/learn/modules/${moduleView.module_id}`);
      }
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setAdvancing(false);
    }
  }

  if (!user) return null;

  if (error) {
    return (
      <div className="min-h-full bg-surface pb-20">
        <TopBar onBack={() => navigate(-1)} title="Урок" />
        <div className="mx-auto max-w-3xl px-4 py-12 text-center text-ink-muted">
          <p>{error}</p>
          <Link
            to="/my-courses"
            className="mt-4 inline-block text-sm text-brand hover:underline"
          >
            ← к моим курсам
          </Link>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-full bg-surface">
        <TopBar onBack={() => navigate(-1)} title="Урок" />
        <main className="mx-auto max-w-3xl px-4 pt-6">
          <div className="h-6 w-2/3 animate-pulse rounded bg-surface-alt" />
          <div className="mt-3 h-3 w-40 animate-pulse rounded bg-surface-alt" />
          <div className="mt-6 h-64 animate-pulse rounded-xl bg-surface-alt" />
        </main>
      </div>
    );
  }

  const completed = progress?.status === "completed";
  const typeLabel = LESSON_TYPE_LABEL[lesson.lesson_type] ?? "Урок";

  // Lesson "X из Y" — position within module
  const positionLabel =
    moduleView && siblings.index >= 0
      ? `${siblings.index + 1} из ${moduleView.lessons.length} уроков`
      : null;

  const lessonNumberLabel =
    moduleView && siblings.index >= 0
      ? `${moduleView.module_position + 1}.${siblings.index + 1}`
      : null;

  // Progress ratio for top bar (1-based: showing lesson N of M)
  const progressPercent =
    moduleView && siblings.index >= 0
      ? Math.round(((siblings.index + 1) / moduleView.lessons.length) * 100)
      : 0;

  return (
    <div className="min-h-full bg-surface pb-28">
      <TopBar
        onBack={() => {
          if (moduleView) {
            navigate(`/learn/modules/${moduleView.module_id}`);
          } else {
            navigate(-1);
          }
        }}
        title={lessonNumberLabel ? `${lessonNumberLabel} ${lesson.title}` : lesson.title}
        showBookmark
      />

      {/* Progress bar */}
      {moduleView && progressPercent > 0 && (
        <div className="border-b border-line bg-surface">
          <div className="mx-auto h-1 max-w-3xl bg-surface-alt">
            <div
              className={`h-full transition-all ${
                completed ? "bg-brand-success" : "bg-brand"
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      <main className="mx-auto max-w-3xl px-4 pt-4">
        {/* Lesson meta */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          {lesson.estimated_minutes && (
            <span className="inline-flex items-center gap-1 text-ink-muted">
              <Clock size={12} strokeWidth={2} />
              {lesson.estimated_minutes} мин
            </span>
          )}
          <span className="text-ink-muted">·</span>
          <span className="font-medium text-brand">{typeLabel}</span>
          {positionLabel && (
            <>
              <span className="ml-auto text-ink-muted">{positionLabel}</span>
            </>
          )}
          {completed && (
            <span className="badge badge-success ml-2">
              <Check size={10} strokeWidth={3} className="mr-0.5" /> Пройден
            </span>
          )}
        </div>

        {lesson.video_url && (
          <div className="mb-6 aspect-video overflow-hidden rounded-xl bg-ink">
            <a
              href={lesson.video_url}
              target="_blank"
              rel="noreferrer"
              className="flex h-full items-center justify-center text-sm text-white underline"
            >
              Открыть видео →
            </a>
          </div>
        )}

        {lesson.content_markdown ? (
          <article className="card p-5 sm:p-6">
            <LessonMarkdown source={lesson.content_markdown} />
          </article>
        ) : (
          <div className="card p-8 text-center text-sm text-ink-muted">
            У этого урока пока нет содержания.
          </div>
        )}
      </main>

      <BottomNav
        prev={siblings.prev}
        moduleView={moduleView}
        onNext={handleNext}
        advancing={advancing}
        completed={completed}
        hasNext={!!siblings.next}
        isLast={!siblings.next && !!moduleView}
      />
    </div>
  );
}

function TopBar({
  onBack,
  title,
  showBookmark = false,
}: {
  onBack: () => void;
  title: string;
  showBookmark?: boolean;
}) {
  return (
    <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
          aria-label="Назад"
        >
          <ChevronLeft size={20} strokeWidth={2} />
        </button>
        <h1 className="mx-2 flex-1 truncate text-center text-sm font-semibold text-ink">
          {title}
        </h1>
        {showBookmark ? (
          <button
            type="button"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Закладка (скоро)"
            disabled
          >
            <Bookmark size={18} strokeWidth={2} />
          </button>
        ) : (
          <span className="h-9 w-9" aria-hidden />
        )}
      </div>
    </header>
  );
}

function BottomNav({
  prev,
  moduleView,
  onNext,
  advancing,
  completed,
  hasNext,
  isLast,
}: {
  prev: ModuleLessonViewOut | null;
  moduleView: StudentModuleViewOut | null;
  onNext: () => void;
  advancing: boolean;
  completed: boolean;
  hasNext: boolean;
  isLast: boolean;
}) {
  const nextLabel = !hasNext
    ? isLast
      ? "Завершить модуль"
      : completed
        ? "Пройдено"
        : "Отметить пройденным"
    : completed
      ? "Далее"
      : "Далее";

  return (
    <div className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-white/95 px-4 py-3 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
        {prev && moduleView ? (
          <Link
            to={`/learn/lessons/${prev.lesson_id}?module=${moduleView.module_id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft transition hover:text-ink"
          >
            <ChevronLeft size={16} strokeWidth={2.25} />
            Назад
          </Link>
        ) : (
          <span aria-hidden className="text-sm text-transparent">
            Назад
          </span>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={advancing || (!hasNext && completed && !isLast)}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {advancing ? "…" : nextLabel}
          {!advancing && hasNext && <ChevronRight size={16} strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
}
